/**
 * What a chair on the bridge is allowed to say.
 *
 * A seat is the only element in the room that looks like set dressing and is
 * not. A figure in a chair is a claim that a named person or session holds a
 * valid lease on that project's workstream right now. So the mapping from a
 * lease record to what is drawn lives here, once, next to its own tests,
 * rather than inside a component that also has geometry to worry about.
 *
 * Three rules decide everything below:
 *
 * 1. A figure is drawn only for a lease that is valid at this moment. Not for
 *    a claim that is still being acquired, not for one that has expired, not
 *    for one that was fenced. Those are chairs with nobody in them, and they
 *    say which of those they are.
 * 2. A lease whose clock has run out is expired, whatever the record still
 *    calls itself. `ACTIVE` with no time left on it is not somebody working;
 *    it is a heartbeat that stopped, and drawing a person there would be the
 *    single most misleading thing this screen could do.
 * 3. The colour comes from `LEASE_STATE_TONE` and nowhere else, which is what
 *    keeps red for a genuine fault. A seat nobody has claimed is neutral; an
 *    expired lease is attention; a contested one is critical, because two
 *    writers on one workstream is a real fault.
 */
import type { ConnectionState, LeaseSummary, ProjectTile, SemanticTone } from '@dcc/shared';
import { LEASE_STATE_TONE } from '@dcc/shared';

export type SeatKind =
  | 'held'
  | 'at-risk'
  | 'contested'
  | 'failover'
  | 'reserved'
  | 'expired'
  | 'fenced'
  | 'fault'
  | 'open';

export interface SeatReading {
  kind: SeatKind;
  /** True only when somebody genuinely holds this console right now. */
  figure: boolean;
  tone: SemanticTone;
  /** The line that names who is there, or says that nobody is. */
  headline: string;
  /** The workstream the lease covers. Null when no lease record exists. */
  workstream: string | null;
  /** Whether this seat can be taken, and if not, why not. */
  availability: string;
  /** The lease clock, when there is one to show. */
  expiry: string | null;
  /** Who would take over. Only ever a name the lease record carries. */
  standby: string | null;
  epoch: number | null;
}

/**
 * What an unclaimed seat can honestly say about itself. A project whose source
 * is not reporting cannot be picked up by pressing the chair, and saying "open
 * to claim" there would be an invitation into a broken connection.
 */
const AVAILABILITY: Record<ConnectionState, string> = {
  DRAFT: 'draft · no source connected yet',
  DISCOVERING: 'discovering the source',
  MAPPING_PROPOSED: 'a source mapping is waiting',
  AWAITING_CONFIRMATION: 'awaiting confirmation',
  VERIFYING: 'verifying the source',
  INITIAL_SYNC: 'first sync running',
  CONNECTED: 'open to claim',
  DEGRADED: 'source degraded',
  RECONNECTING: 'source reconnecting',
  FAILED: 'connection failed',
  UNLINKED: 'no source linked',
  ARCHIVED: 'archived',
};

/**
 * Has the clock run out? `secondsRemaining` is the projection's own count and
 * is preferred; `expiresAt` is the fallback and is compared against the
 * browser's clock, which is why it is only trusted when the count is absent.
 */
function laterThanItsLease(lease: LeaseSummary, now: Date): boolean {
  if (lease.secondsRemaining !== null) return lease.secondsRemaining <= 0;
  if (lease.expiresAt !== null) return Date.parse(lease.expiresAt) <= now.getTime();
  return false;
}

function who(lease: LeaseSummary): string {
  return lease.ownerName ?? lease.ownerSessionId ?? 'unnamed session';
}

function clock(lease: LeaseSummary): string | null {
  if (lease.secondsRemaining === null) return null;
  const seconds = Math.max(0, lease.secondsRemaining);
  if (seconds === 0) return 'lease ran out';
  return seconds < 90
    ? `lease ends in ${Math.round(seconds)}s`
    : `lease ends in ${Math.round(seconds / 60)}m`;
}

export function readSeat(tile: ProjectTile, now: Date = new Date()): SeatReading {
  const availability = AVAILABILITY[tile.connectionState] ?? 'availability unknown';
  const lease = tile.captain;

  // No lease record at all. Not "nobody is working" — nobody has claimed this
  // console, which is a different and smaller statement.
  if (!lease) {
    return {
      kind: 'open',
      figure: false,
      tone: 'neutral',
      headline: 'seat open',
      workstream: null,
      availability,
      expiry: null,
      standby: null,
      epoch: null,
    };
  }

  const base = {
    workstream: lease.workstream,
    standby: lease.standbyName,
    epoch: lease.epoch,
    expiry: clock(lease),
  };
  const tone = LEASE_STATE_TONE[lease.state];

  const expired = laterThanItsLease(lease, now);

  switch (lease.state) {
    case 'ACTIVE':
    case 'AT_RISK':
      // Rule 2. A record that still calls itself active but has no time left is
      // an expired lease, and the chair is empty.
      if (expired) {
        return {
          ...base,
          kind: 'expired',
          figure: false,
          tone: LEASE_STATE_TONE.EXPIRED,
          headline: `${who(lease)} · lease expired`,
          availability: 'the lease ran out; the seat is open again',
          expiry: 'lease ran out',
        };
      }
      return {
        ...base,
        kind: lease.state === 'AT_RISK' ? 'at-risk' : 'held',
        figure: true,
        tone,
        headline: `${who(lease)} · epoch ${lease.epoch}`,
        availability:
          lease.state === 'AT_RISK'
            ? 'held, but the heartbeat is late'
            : `held · ${lease.protection === 'FENCED' ? 'fenced' : 'unprotected'}`,
      };

    case 'SPLIT_BRAIN_DETECTED':
      // Two writers on one workstream. Somebody is there — that is exactly the
      // problem — so the figure stays and is marked as contested.
      return {
        ...base,
        kind: 'contested',
        figure: true,
        tone,
        headline: `${who(lease)} · epoch ${lease.epoch}`,
        availability: `contested · a second claim reached epoch ${lease.highestAcceptedEpoch}`,
      };

    case 'TAKING_OVER':
      // The old holder is being fenced and the new one is not up yet. Nobody
      // is at this console during the handover, so no figure.
      return {
        ...base,
        kind: 'failover',
        figure: false,
        tone,
        headline: lease.standbyName ? `handing over to ${lease.standbyName}` : 'handing over',
        availability: 'failover in progress; the console is not staffed',
      };

    case 'ACQUIRING':
      // Reserved, not occupied. This is the state the interface used to have no
      // word for, and drawing it as either taken or free was wrong both ways.
      return {
        ...base,
        kind: 'reserved',
        figure: false,
        tone,
        headline: lease.ownerName ? `reserved by ${lease.ownerName}` : 'reserved',
        availability: 'a claim is being acquired; nobody is at the console yet',
      };

    case 'EXPIRED':
      return {
        ...base,
        kind: 'expired',
        figure: false,
        tone,
        headline: `${who(lease)} · lease expired`,
        availability: 'the lease ran out; the seat is open again',
        expiry: 'lease ran out',
      };

    case 'FENCED':
      return {
        ...base,
        kind: 'fenced',
        figure: false,
        tone,
        headline: `${who(lease)} · fenced out`,
        availability: `an older claim was cut off at epoch ${lease.epoch}`,
      };

    case 'ERROR':
      return {
        ...base,
        kind: 'fault',
        figure: false,
        tone,
        headline: 'lease error',
        availability: 'the lease could not be read; nobody is assumed to be here',
      };

    case 'NONE':
    case 'RELEASED':
    default:
      // A released lease leaves a real workstream behind, which is more than
      // "no record" says, so it is kept on the label.
      return {
        ...base,
        kind: 'open',
        figure: false,
        tone: 'neutral',
        headline: 'seat open',
        availability,
        expiry: null,
      };
  }
}
